import { createContext, useContext, useState, type ReactNode } from "react";
import { Form, Link, useSearchParams } from "react-router";
import type { Leaderboard, LeaderboardRow, Overall, OverallRow, SpaceEvent, TeamLine } from "@repo/shared-types";
import { partLabel, rankingLabel } from "@/lib/event-form";
import { TEAM_DIVISIONS, memberAsPerson, teamNames } from "@/lib/teams";
import { Feel, RxPlusBadge } from "@/components/result-fields";
import { ALL_DIVISIONS, DIVISIONS, LEVELS, ordinal, overallHasLevels, resultsOn, type Board } from "@/lib/results";
import { boardAddress } from "@/lib/boards";
import { PhotoMark } from "@/components/photos";
import { Avatar, AvatarStack } from "@/components/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyNote } from "@/components/empty-note";
import { Trophy } from "lucide-react";
import { tabsListVariants } from "@/components/ui/tabs";
import { ConfirmDialog } from "@/components/modal";
import { cn } from "@/lib/utils";

/**
 * An event's leaderboards: one per part with a leaderboard, per level and
 * division — and, for an event ranked overall, its overall ranking, one per
 * level and division too. Each is a screen of its own since 10 October
 * (routes/board.tsx), linked from its part on the event's page. Which board
 * it shows lives in the address (lib/boards.ts), so a link to the screen is a
 * link to the board as it was. Every tab says how many results it has.
 *
 * Only shared results are ever here; the api sends nothing else. The owner and
 * admins holding moderate hide one on one part's board, which leaves it in its
 * athlete's log until they put it back from the event's hidden results; the
 * api sends its athlete's own as the board's hidden line, and nobody else's.
 * An overall counts what the parts show, so it has nothing to hide of its own.
 *
 * A verified result is marked, and so is an unverified one on a verifiable
 * part; the owner and admins holding verify_scores verify an unverified line,
 * or take a verification back, from the line itself. A result waiting for
 * verification — late — is on no line: the api sends its athlete's own as
 * the board's waiting line, pinned above it without a rank, and nobody
 * else's. Verifiers find those in the queue.
 *
 * A part's division tabs end on All (0089): every division of the level on
 * one board, its tab counting them all.
 *
 * A part done in teams (0027) ranks teams, one line per team, on boards per
 * level and per division — men, women or mixed. A line names the people the
 * api sends — its guests and those who shared — and counts the rest, and
 * links to the team's own page. The viewer's own team — one a part, since
 * 0036 — is marked, and pinned when it is off the page.
 */

/** The board a card shows: a part's, or the overall ranking. */
export type BoardData =
  | { kind: "part"; choice: Board; board: Leaderboard; cursor: string | null }
  | { kind: "overall"; choice: Board; board: Overall; cursor: string | null };

/** Every board an event's page shows: each ranked part's by its id, and the overall's. */
export type EventBoards = { parts: Record<string, BoardData>; overall: BoardData | null };

/**
 * The event's page, which a line's athlete and team link under. Absolute, so
 * a board links the same wherever it is drawn.
 */
const EventPath = createContext("");

/**
 * A tab of a board: one segment of its level's or its division's control.
 * A link, not shadcn's tab: which board is shown lives in the address, so
 * a tab is a page of its own to open, share and go back to. Drawn as
 * shadcn's tabs are, the one shown raised from the list.
 */
function Tab({ to, current, empty, children }: { to: string; current: boolean; empty: boolean; children: ReactNode }) {
  return (
    <Link
      to={to}
      preventScrollReset
      aria-current={current ? "page" : undefined}
      className={cn(
        "flex h-full flex-1 items-center justify-center whitespace-nowrap rounded-full border border-transparent px-3 text-sm font-medium transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
        current
          ? "bg-background text-foreground shadow-sm dark:border-input dark:bg-input/30"
          : empty
            ? "text-muted-foreground/70 hover:text-foreground"
            : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </Link>
  );
}

const segments = cn(tabsListVariants(), "flex h-10 w-full");

/**
 * One board, with its level and division tabs and its pages: a part's, in
 * its card, or the overall ranking's. `label` names it for whoever reads the
 * page by its regions — "Part A leaderboard", "Overall ranking".
 */
export function EventBoard({
  event,
  eventPath,
  data,
  label,
  canModerate,
  canVerify,
  busy,
}: {
  event: SpaceEvent;
  /** The event's page, which every line's result and team are addressed under. */
  eventPath: string;
  data: BoardData;
  label: string;
  canModerate: boolean;
  canVerify: boolean;
  busy: boolean;
}) {
  const [search] = useSearchParams();
  const { board, choice, cursor } = data;
  const part = event.parts.find((candidate) => candidate.id === choice.partId);
  const levelled = data.kind === "overall" ? overallHasLevels(event) : (part?.usesLevels ?? false);
  const mineOffPage = board.mine && !board.rows.some((row) => row.entryId === board.mine?.entryId);
  const waiting = data.kind === "part" ? data.board.waiting : null;
  const hidden = data.kind === "part" ? data.board.hidden : null;
  const teams = data.kind === "part" && (part?.isTeam ?? false) ? data.board : null;
  // A part's board ends on All, every division at once (0089); an overall's
  // keeps men and women apart.
  const divisions =
    data.kind === "overall" ? DIVISIONS : [...(teams ? TEAM_DIVISIONS : DIVISIONS), ALL_DIVISIONS];
  const empty = teams
    ? teams.teams.length === 0 && teams.myTeams.length === 0 && teams.waitingTeams.length === 0 && teams.hiddenTeams.length === 0
    : board.rows.length === 0 && !waiting && !hidden;
  const address = (to: Board, page?: string | null) => boardAddress(search, choice.partId, { ...to, cursor: page });

  return (
    <EventPath value={eventPath}>
    <section className="space-y-3" aria-label={label}>
      {/* A row each on a phone, side by side where they fit. */}
      <div className="grid gap-2 sm:auto-cols-fr sm:grid-flow-col">
        {levelled && (
          <nav className={segments} aria-label="Level">
            {LEVELS.map((level) => {
              const count = resultsOn(board, level.value, choice.division);
              return (
                <Tab
                  key={level.value}
                  to={address({ ...choice, level: level.value })}
                  current={choice.level === level.value}
                  empty={count === 0}
                >
                  {level.label} ({count})
                </Tab>
              );
            })}
          </nav>
        )}
        <nav className={segments} aria-label="Division">
          {divisions.map((division) => {
            const count = resultsOn(board, choice.level, division.value);
            return (
              <Tab
                key={division.value}
                to={address({ ...choice, division: division.value })}
                current={choice.division === division.value}
                empty={count === 0}
              >
                {division.label} ({count})
              </Tab>
            );
          })}
        </nav>
      </div>

      {empty && !cursor ? (
        <EmptyNote icon={Trophy}>{teams ? "No teams on this board yet." : "No results on this board yet."}</EmptyNote>
      ) : teams ? (
        <TeamLines board={teams} canModerate={canModerate} canVerify={canVerify} busy={busy} />
      ) : data.kind === "overall" ? (
        <Lines>
          {mineOffPage && data.board.mine && <OverallLine row={data.board.mine} event={event} mine pinned />}
          {data.board.rows.map((row) => (
            <OverallLine key={row.entryId} row={row} event={event} mine={row.entryId === data.board.mine?.entryId} />
          ))}
        </Lines>
      ) : (
        <Lines>
          {hidden && (
            <OffTheBoardLine row={hidden} tone="text-danger">
              Hidden by the space: a moderator took it off this board until they put it back. Only you see this
              line.
            </OffTheBoardLine>
          )}
          {waiting && (
            <OffTheBoardLine row={waiting} tone="text-warning">
              Waiting for verification: it changed, or was shared, after the deadline. Only you see this line.
            </OffTheBoardLine>
          )}
          {mineOffPage && data.board.mine && (
            <Row
              row={data.board.mine}
              partId={choice.partId}
              mine
              canModerate={canModerate}
              canVerify={canVerify}
              busy={busy}
              pinned
            />
          )}
          {data.board.rows.map((row) => (
            <Row
              key={row.entryId}
              row={row}
              partId={choice.partId}
              mine={row.entryId === data.board.mine?.entryId}
              canModerate={canModerate}
              canVerify={canVerify}
              busy={busy}
            />
          ))}
        </Lines>
      )}

      {(cursor || board.nextCursor) && (
        <nav className="flex justify-between" aria-label="Pages">
          {cursor ? (
            <Button asChild variant="outline" size="lg">
              <Link to={address(choice)} preventScrollReset>
                ← Top
              </Link>
            </Button>
          ) : (
            <span />
          )}
          {board.nextCursor && (
            <Button asChild variant="outline" size="lg">
              <Link to={address(choice, board.nextCursor)} preventScrollReset>
                Next →
              </Link>
            </Button>
          )}
        </nav>
      )}
    </section>
    </EventPath>
  );
}

/** A board's lines: one card, a line under another. */
function Lines({ children }: { children: ReactNode }) {
  return (
    <Card className="gap-0 py-0">
      <ol className="divide-y divide-border">{children}</ol>
    </Card>
  );
}

/** A line's place: large, the podium in the preset's strongest colour. */
function Rank({ rank }: { rank: number }) {
  return (
    <span
      className={cn(
        "w-8 shrink-0 text-center text-2xl font-semibold tracking-tight tabular-nums",
        rank > 0 && rank <= 3 ? "text-primary" : "text-muted-foreground",
      )}
    >
      {rank}
    </span>
  );
}

/** The rank's place on a line off the board: none. */
function NoRank() {
  return <span className="w-8 shrink-0 text-center text-2xl font-semibold text-muted-foreground">—</span>;
}

/** Where the viewer's own line is: tinted. */
const mineLine = "bg-muted";

/**
 * A line's score: the number people look for, in the board's voice — and in
 * the queue's and the hidden list's, which show the same results.
 */
export const scoreText = "whitespace-nowrap text-xl font-semibold leading-none tracking-tight tabular-nums";

/**
 * A line: its place, its picture, who and what they can do about it, then
 * the score. Square inside the board's card, a rule between each.
 */
function Line({ className, children }: { className?: string; children: ReactNode }) {
  return <li className={cn("flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 text-sm", className)}>{children}</li>;
}

/** How an event ranked overall is ranked, said above its board. */
export function OverallRule({ event }: { event: SpaceEvent }) {
  // A part done in teams counts for each of a team's people (0037).
  const teams = event.parts.some((part) => part.isTeam && part.hasLeaderboard);
  return (
    <p className="text-xs text-muted-foreground">
      {rankingLabel(event.ranking)}
      {event.ranking === "placement"
        ? ": a place on each part is its points, and a part not done scores one past its last place. Fewest points wins; a tie goes to the most first places, then seconds."
        : ": each part's result added up. More parts done ranks first — a capped time is not done — then the total."}
      {teams &&
        (event.ranking === "placement"
          ? " A part done in teams ranks the teams, and each athlete takes their team's place."
          : " A part done in teams adds the team's score for each of its people.")}
    </p>
  );
}

/**
 * A team part's board: the viewer's own teams while hidden or waiting, theirs
 * that fall off the page, then the page.
 */
function TeamLines({
  board,
  canModerate,
  canVerify,
  busy,
}: {
  board: Leaderboard;
  canModerate: boolean;
  canVerify: boolean;
  busy: boolean;
}) {
  const mine = new Set(board.myTeams.map((line) => line.team.id));
  const onPage = new Set(board.teams.map((line) => line.team.id));
  return (
    <Lines>
      {board.hiddenTeams.map((line) => (
        <OffTheBoardTeamLine key={line.team.id} line={line} tone="text-danger">
          Hidden by the space: a moderator took your team off this board until they put it back. Only your team
          sees this line.
        </OffTheBoardTeamLine>
      ))}
      {board.waitingTeams.map((line) => (
        <OffTheBoardTeamLine key={line.team.id} line={line} tone="text-warning">
          Waiting for verification: its score changed, or it went on the board, after the deadline. Only your team
          sees this line.
        </OffTheBoardTeamLine>
      ))}
      {board.myTeams
        .filter((line) => !onPage.has(line.team.id))
        .map((line) => (
          <TeamRow key={line.team.id} line={line} mine pinned canModerate={canModerate} canVerify={canVerify} busy={busy} />
        ))}
      {board.teams.map((line) => (
        <TeamRow
          key={line.team.id}
          line={line}
          mine={mine.has(line.team.id)}
          canModerate={canModerate}
          canVerify={canVerify}
          busy={busy}
        />
      ))}
    </Lines>
  );
}

/** A team's names on its line, linking to its own page — relative to the event's. */
function TeamName({ line }: { line: TeamLine }) {
  const eventPath = useContext(EventPath);
  return (
    <Link to={`${eventPath}/teams/${line.team.id}`} className="font-medium hover:underline">
      {teamNames(line.team) || "A team"}
    </Link>
  );
}

/** A team's people, overlapping, in a line's picture column. */
function TeamPictures({ line }: { line: TeamLine }) {
  return <AvatarStack people={line.team.members.map(memberAsPerson)} />;
}

function TeamRow({
  line,
  mine,
  pinned = false,
  canModerate,
  canVerify,
  busy,
}: {
  line: TeamLine;
  mine: boolean;
  pinned?: boolean;
  canModerate: boolean;
  canVerify: boolean;
  busy: boolean;
}) {
  const [first, ...tiebreaks] = line.team.scores;
  const notes = line.team.members.filter((member) => member.note);
  return (
    <Line className={cn(mine && mineLine)}>
      <Rank rank={line.rank} />
      <TeamPictures line={line} />
      <span className="min-w-0 flex-1">
        <TeamName line={line} />
        {mine && <span className="text-muted-foreground">{pinned ? " — your team" : " (your team)"}</span>}
        {line.team.rxPlus && <RxPlusBadge />}
        {notes.map((member) => (
          <span key={member.id} className="block whitespace-pre-line text-xs text-muted-foreground">
            @{member.user?.username}: {member.note}
          </span>
        ))}
      </span>
      <span className="shrink-0 text-right">
        <span className={scoreText}>{first?.display}</span>
        {tiebreaks
          .filter((tiebreak) => tiebreak.display)
          .map((tiebreak, index) => (
            <span key={index} className="block text-xs text-muted-foreground">
              then {tiebreak.display}
            </span>
          ))}
        <VerificationMark verification={line.verification} verifiedBy={line.verifiedBy} />
      </span>
      <LineActions>
        {canVerify && line.changedAt && line.verification === "unverified" && (
          <VerifyResult intent="verify" label="Verify" teamId={line.team.id} changedAt={line.changedAt} busy={busy} />
        )}
        {canVerify && line.verification === "verified" && (
          <VerifyResult intent="unverify" label="Remove verification" teamId={line.team.id} busy={busy} />
        )}
        {canModerate && <HideResult teamId={line.team.id} busy={busy} />}
      </LineActions>
    </Line>
  );
}

/** One of the viewer's teams while it is off the board, without a rank, with why. */
function OffTheBoardTeamLine({ line, tone, children }: { line: TeamLine; tone: string; children: ReactNode }) {
  return (
    <Line className="bg-warning-muted">
      <NoRank />
      <TeamPictures line={line} />
      <span className="min-w-0 flex-1">
        <TeamName line={line} />
        <span className="text-muted-foreground"> — your team</span>
        {line.team.rxPlus && <RxPlusBadge />}
        <span className={`block text-xs ${tone}`}>{children}</span>
      </span>
      <span className={cn(scoreText, "shrink-0")}>{line.team.scores[0]?.display}</span>
    </Line>
  );
}

/**
 * One athlete on an overall board: their points or their total, and where
 * they stand on each part that counts — "A 1st · 4:12".
 */
function OverallLine({
  row,
  event,
  mine,
  pinned = false,
}: {
  row: OverallRow;
  event: SpaceEvent;
  mine: boolean;
  pinned?: boolean;
}) {
  const label = (partId: string) => partLabel(event.parts.findIndex((part) => part.id === partId));
  return (
    <Line className={cn(mine && mineLine)}>
      <Rank rank={row.rank} />
      <Avatar person={row.athlete} size="md" />
      <span className="min-w-0 flex-1">
        <Athlete entryId={row.entryId} athlete={row.athlete} />
        {mine && <span className="text-muted-foreground">{pinned ? " — you" : " (you)"}</span>}
        <span className="block text-xs text-muted-foreground">
          {row.parts
            .map((part) => {
              const place = part.place !== null ? `${ordinal(part.place)} · ` : "";
              const badge = part.rxPlus ? " Rx+" : "";
              const shown = part.score.display ? place + part.score.display + badge + mark(part.verification) : "—";
              // Their team's place and score, on a part done in teams.
              return `${label(part.partId)} ${shown}${part.teamId ? " (team)" : ""}`;
            })
            .join("   ")}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className={scoreText}>{row.points !== null ? `${row.points} pts` : (row.total?.display ?? "—")}</span>
        {row.partsDone < row.parts.length && (
          <span className="block text-xs text-muted-foreground">
            {row.partsDone} of {row.parts.length} parts
          </span>
        )}
      </span>
    </Line>
  );
}

/**
 * An athlete on a line, linking to their result's own page — its reactions
 * and its thread. Relative to the event's page, which every board is on.
 */
function Athlete({ entryId, athlete }: { entryId: string; athlete: LeaderboardRow["athlete"] }) {
  const eventPath = useContext(EventPath);
  return (
    <>
      <Link to={`${eventPath}/results/${entryId}`} className="font-medium hover:underline">
        {athlete.name ?? `@${athlete.username}`}
      </Link>
      {athlete.name && <span className="text-muted-foreground"> @{athlete.username}</span>}
    </>
  );
}

/**
 * What a verifier or moderator can do about a line: a row of its own under
 * it, lined up with the name, in small buttons. Nothing, for anybody else.
 * The longest it gets — Remove verification, then Hide… — fits beside the
 * indent on a 390-pixel phone, so a line never grows sideways.
 */
function LineActions({ children }: { children: ReactNode }) {
  const any = (Array.isArray(children) ? children : [children]).some(Boolean);
  return any ? <div className="flex basis-full flex-wrap items-center gap-2 pl-[5.75rem]">{children}</div> : null;
}

/**
 * How a part's result stands, after its score on an overall line: " ✓" when
 * verified, " (unverified)" on a verifiable part, " (waiting)" or " (hidden)"
 * on the viewer's own line while it is off its board.
 */
function mark(verification: string): string {
  switch (verification) {
    case "verified":
      return " ✓";
    case "unverified":
      return " (unverified)";
    case "waiting":
      return " (waiting)";
    case "hidden":
      return " (hidden)";
    default:
      // "" says nothing, and a standing this build does not know says
      // nothing it could get wrong.
      return "";
  }
}

/** A line's verification, as every member reads it: an athlete's or a team's. */
function VerificationMark({ verification, verifiedBy }: Pick<LeaderboardRow, "verification" | "verifiedBy">) {
  if (verification === "verified") {
    const by = verifiedBy ? ` by @${verifiedBy.username}` : "";
    return (
      <span className="block text-xs font-medium text-success" title={`Verified${by}`}>
        ✓ Verified
      </span>
    );
  }
  if (verification === "unverified") return <span className="block text-xs text-muted-foreground">unverified</span>;
  return null;
}

/**
 * The viewer's own result while it is off the board — waiting for
 * verification, or hidden by the space: without a rank, and theirs alone to
 * see, with why.
 */
function OffTheBoardLine({ row, tone, children }: { row: LeaderboardRow; tone: string; children: ReactNode }) {
  const eventPath = useContext(EventPath);
  return (
    <Line className="bg-warning-muted">
      <NoRank />
      <Avatar person={row.athlete} size="md" />
      <span className="min-w-0 flex-1">
        <Link to={`${eventPath}/results/${row.entryId}`} className="font-medium hover:underline">
          {row.athlete.name ?? `@${row.athlete.username}`}
        </Link>
        <span className="text-muted-foreground"> — you</span>
        {row.rxPlus && <RxPlusBadge />}
        <span className={`block text-xs ${tone}`}>{children}</span>
      </span>
      <span className={cn(scoreText, "shrink-0")}>{row.scores[0]?.display}</span>
    </Line>
  );
}

function Row({
  row,
  partId,
  mine,
  pinned = false,
  canModerate,
  canVerify,
  busy,
}: {
  row: LeaderboardRow;
  partId: string;
  mine: boolean;
  pinned?: boolean;
  canModerate: boolean;
  canVerify: boolean;
  busy: boolean;
}) {
  const [first, ...tiebreaks] = row.scores;
  const eventPath = useContext(EventPath);
  return (
    <Line className={cn(mine && mineLine)}>
      <Rank rank={row.rank} />
      <Avatar person={row.athlete} size="md" />
      <span className="min-w-0 flex-1">
        <Athlete entryId={row.entryId} athlete={row.athlete} />
        {mine && <span className="text-muted-foreground">{pinned ? " — you" : " (you)"}</span>}
        {row.rxPlus && <RxPlusBadge />}
        {row.hasPhoto && <PhotoMark to={`${eventPath}/results/${row.entryId}`} />}
        {row.note && <span className="block whitespace-pre-line text-xs text-muted-foreground">{row.note}</span>}
      </span>
      <span className="shrink-0 text-right">
        {row.feel && (
          <span className="mr-1">
            <Feel feel={row.feel} />
          </span>
        )}
        <span className={scoreText}>{first?.display}</span>
        {tiebreaks
          .filter((tiebreak) => tiebreak.display)
          .map((tiebreak, index) => (
            <span key={index} className="block text-xs text-muted-foreground">
              then {tiebreak.display}
            </span>
          ))}
        <VerificationMark verification={row.verification} verifiedBy={row.verifiedBy} />
      </span>
      <LineActions>
        {canVerify && row.changedAt && row.verification === "unverified" && (
          <VerifyResult intent="verify" label="Verify" partId={partId} entryId={row.entryId} changedAt={row.changedAt} busy={busy} />
        )}
        {canVerify && row.verification === "verified" && (
          <VerifyResult intent="unverify" label="Remove verification" partId={partId} entryId={row.entryId} busy={busy} />
        )}
        {canModerate && <HideResult partId={partId} entryId={row.entryId} busy={busy} />}
      </LineActions>
    </Line>
  );
}

/**
 * Verifying a result as the line shows it — its clock goes back with it, so
 * one changed since is refused rather than verified unseen — or taking a
 * verification back. The page's action does either: a toast once it has, a
 * refusal at the top of the page.
 */
export function VerifyResult({
  intent,
  label,
  partId,
  entryId,
  teamId,
  changedAt,
  variant = "outline",
  busy,
}: {
  intent: "verify" | "unverify";
  label: string;
  /** An athlete's result, as a board names it — or a team's, by its id. */
  partId?: string;
  entryId?: string;
  teamId?: string;
  changedAt?: string;
  /** Filled where verifying is what the page is for, the queue; outlined on a board. */
  variant?: "default" | "outline";
  busy: boolean;
}) {
  return (
    <Form method="post" className="shrink-0">
      <input type="hidden" name="intent" value={intent} />
      <ResultFields partId={partId} entryId={entryId} teamId={teamId} />
      {changedAt && <input type="hidden" name="changedAt" value={changedAt} />}
      <Button type="submit" variant={variant} size="sm" disabled={busy}>
        {label}
      </Button>
    </Form>
  );
}

/**
 * Hides a result on one part's board, until a moderator puts it back:
 * moderation, never deletion. Asked first, in the shared sheet: a popover
 * here was cut off by a board's rounded box, and ran past a phone's edge
 * from the end of a line. The page's action does it, and a refusal stays in
 * the sheet.
 */
export function HideResult({
  partId,
  entryId,
  teamId,
  busy,
}: {
  partId?: string;
  entryId?: string;
  teamId?: string;
  busy: boolean;
}) {
  const [asking, setAsking] = useState(false);
  return (
    <>
      <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setAsking(true)}>
        Hide…
      </Button>
      {asking && (
        <ConfirmDialog
          title={teamId ? "Hide this team on this part's board?" : "Hide this result on this part's board?"}
          fields={{ intent: "hide", ...resultFields({ partId, entryId, teamId }) }}
          confirm="Hide"
          onClose={() => setAsking(false)}
        >
          {teamId
            ? "It stays in its people's logs, and those who shared are told the space hid it."
            : "It stays in its athlete's log, and they're told the space hid it."}{" "}
          Put it back from the event's hidden results.
        </ConfirmDialog>
      )}
    </>
  );
}

type ResultAddress = { partId?: string; entryId?: string; teamId?: string };

/**
 * What a verification or a hiding is about, for the page's action: an
 * athlete's result by its part and entry, or a team by its id.
 */
export function resultFields({ partId, entryId, teamId }: ResultAddress): Record<string, string> {
  return teamId ? { team: teamId } : { part: partId ?? "", entry: entryId ?? "" };
}

/** The same, as a form's hidden inputs. */
export function ResultFields(address: ResultAddress) {
  return (
    <>
      {Object.entries(resultFields(address)).map(([name, value]) => (
        <input key={name} type="hidden" name={name} value={value} />
      ))}
    </>
  );
}
